import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaFileSignature, FaCheck, FaSpinner } from "react-icons/fa";

const PDFPreviewModal = ({ pdfBlob, onClose, onConfirm, isSubmitting }) => {
  const [pdfUrl, setPdfUrl] = useState("");
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [pdfBlob]);

  useEffect(() => {
    if (!modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [modalRoot]);

  const handleConfirm = () => {
    // Automatically add current date
    const currentDate = new Date().toISOString().split('T')[0];
    
    onConfirm({
      signatureDate: currentDate
    });
  };

  if (!modalRoot) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-[95%] max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              PDF Preview
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Review your checklist before submission
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
          >
            <FaTimes className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* PDF Preview */}
          <div className="bg-gray-50 dark:bg-dark-900 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
              <FaFileSignature className="mr-2 text-primary-500" />
              Checklist Preview
            </h3>
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-[500px] border border-gray-300 dark:border-dark-700 rounded-lg"
                title="PDF Preview"
              />
            ) : (
              <div className="w-full h-[500px] flex items-center justify-center bg-white dark:bg-dark-800 rounded-lg">
                <div className="text-center">
                  <FaSpinner className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">Loading PDF...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900 rounded-b-2xl">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            By confirming, you agree that the information provided is accurate. Date will be added automatically.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600 transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FaCheck />
                  Confirm & Submit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default PDFPreviewModal;
